import { useState, useEffect } from 'react'
import PublicLayout from '@/components/layout/PublicLayout'
import { detectRegion, type Region } from '@/utils/time'
import { REGION_DATA } from '@/components/layout/HealthDropdown'

interface Feature {
  title: string
  description: string
}

interface FeatureSection {
  id: string
  icon: React.ReactNode
  title: string
  subtitle: string
  features: Feature[]
}

export default function Features({ embedded }: { embedded?: boolean }) {
  const [region, setRegion] = useState<Region>(detectRegion)

  useEffect(() => {
    const handler = (e: Event) => setRegion((e as CustomEvent).detail)
    window.addEventListener('ssdl:region-change', handler)
    return () => window.removeEventListener('ssdl:region-change', handler)
  }, [])

  // Scroll to hash anchor on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1)
    if (hash) {
      const el = document.getElementById(hash)
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100)
    }
  }, [])

  const rd = REGION_DATA[region]

  const sections: FeatureSection[] = [
    {
      id: 'bac-tracking',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
      title: 'Real-Time BAC Tracking',
      subtitle: 'Know exactly where you stand',
      features: [
        { title: 'Live BAC calculation', description: 'Blood alcohol content calculated using the Widmark formula, updated in real-time as you log drinks' },
        { title: 'Colour-coded status', description: 'BAC display changes colour based on severity — green when sober, through amber and red as levels rise' },
        { title: 'Time until sober', description: 'Live countdown showing exactly how long until your BAC reaches zero, with explanations at each level' },
        { title: 'Session awareness', description: 'Compare your current session to your historical patterns and get real-time feedback' },
      ],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      ),
      id: 'quick-logging',
      title: 'Quick & Easy Logging',
      subtitle: 'Log a drink in seconds',
      features: [
        { title: '65+ pre-loaded drinks', description: 'Database of common beers, wines, spirits, and cocktails with volumes and ABV pre-filled' },
        { title: 'AI drink lookup', description: 'Can\'t find your drink? Type the name and AI will find the volume, ABV, and calories for you' },
        { title: 'Custom drink templates', description: 'Save your regular drinks for one-tap logging next time' },
        { title: 'Gap logging', description: 'Record periods you weren\'t tracking to keep your sober streak calculations accurate' },
      ],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      id: 'session-insights',
      title: 'Insights & Patterns',
      subtitle: 'See the full picture, not just tonight',
      features: [
        { title: 'Session history', description: 'Browse your last 5 sessions with peak BAC, drink count, and duration for each' },
        { title: 'Sobriety streaks', description: 'Track your top 5 longest sober periods — gap-aware so only real tracked time counts' },
        { title: 'Worst cases', description: 'See your highest BAC and most drinks in a single session, with dates' },
        { title: 'Trends chart', description: 'Dual-axis chart plotting peak BAC and drink count over time — filter by week, month, year, or all time' },
        { title: 'Full drink history', description: 'Searchable, paginated log of every drink with time gaps between entries' },
      ],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
        </svg>
      ),
      id: 'health-risk',
      title: 'Health & Risk Assessment',
      subtitle: `Based on ${rd.guidelinesBodyFull} guidelines`,
      features: [
        { title: 'Automatic risk detection', description: `Your consumption is classified as Low, Increased, or High risk against ${rd.guidelinesBody} guidelines` },
        { title: 'Proactive health alerts', description: 'A heart icon in the nav bar changes appearance when your drinking exceeds recommended limits' },
        { title: 'Healthy drinking info', description: `Region-specific guidance — ${rd.guidelinesWeekly}, and no more than ${rd.guidelinesSession}` },
        { title: 'Standard drink calculator', description: rd.stdDrinkNote + ' Examples: ' + rd.stdDrinkExamples[0] },
      ],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
      id: 'gp-reports',
      title: 'GP-Ready Reports',
      subtitle: 'Walk into your appointment with data',
      features: [
        { title: 'Professional PDF report', description: 'Download a clinical-style alcohol consumption report designed for doctor appointments' },
        { title: 'Consumption summary', description: 'Total and average drinks, standard drinks, peak and average session BAC' },
        { title: 'Risk classification', description: `Weekly and per-session consumption rated against ${rd.guidelinesBody} guidelines` },
        { title: '30-day breakdown', description: 'Week-by-week consumption for the last month, plus sobriety patterns and worst cases' },
      ],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
      ),
      id: 'country-support',
      title: 'Country-Specific Support',
      subtitle: `Currently showing for ${rd.flag} ${rd.label}`,
      features: [
        { title: 'Regional measurements', description: region === 'US' ? 'Weights in pounds, volumes in fluid ounces — all converted automatically' : 'Weights in kilograms, volumes in millilitres — metric by default' },
        { title: 'Local date formats', description: region === 'US' ? 'Dates displayed as mm/dd/yyyy to match US conventions' : 'Dates displayed as dd/mm/yyyy to match local conventions' },
        { title: 'Crisis helplines', description: `${rd.helplines.map(h => h.name).join(', ')} — all free and confidential` },
        { title: 'Local health guidelines', description: `${rd.guidelinesBodyFull} recommendations for safe drinking limits` },
        { title: 'Emergency number', description: `Call ${rd.emergency} in an emergency — displayed throughout the app` },
      ],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
      ),
      id: 'encryption',
      title: 'Privacy & Security',
      subtitle: 'Your data belongs to you',
      features: [
        { title: 'No account required', description: 'Just a passcode — no email, no sign-up, no personal information needed to start' },
        { title: 'End-to-end encryption', description: 'Set a password and all sensitive data is encrypted on your device before it ever reaches our servers' },
        { title: 'AES-256-GCM encryption', description: 'Military-grade encryption with PBKDF2 key derivation (100,000 iterations) — even we can\'t read your data' },
        { title: 'Zero tracking', description: 'No analytics, no cookies, no third-party scripts — we don\'t track you at all' },
        { title: 'No AI on your data', description: 'AI is only used to look up drink info by name. Your drink log, BAC history, and personal data are never sent to any AI service' },
        { title: 'Password protection', description: 'Optional password with flexible modes — lock on every visit, or after a timeout period' },
        { title: 'Auto-lock & auto-logout', description: 'Configurable inactivity timeout that locks or fully logs you out and wipes local data' },
      ],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
        </svg>
      ),
      id: 'data-management',
      title: 'Data Management',
      subtitle: 'Full control over your data',
      features: [
        { title: 'Automatic backups', description: 'Last 3 versions of your data are saved automatically every time you log a drink' },
        { title: 'Download your data', description: 'Export everything as a JSON file — your data is always portable' },
        { title: 'Import data', description: 'Upload a previously exported file to restore or migrate your drink log' },
        { title: 'Key recovery', description: 'Optionally save a backup email to recover your passcode if you lose it' },
        { title: 'Delete everything', description: 'Full data deletion — removes all entries, backups, and email index permanently' },
      ],
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
        </svg>
      ),
      id: 'free-forever',
      title: 'Free Forever',
      subtitle: 'No catches, no subscriptions',
      features: [
        { title: 'Completely free', description: 'Every feature is available to everyone, always. No premium tier, no paywalls, no ads' },
        { title: 'No sign-up required', description: 'Start tracking in seconds with just a passcode — nothing else needed' },
        { title: 'Unlimited history', description: 'Log as many drinks as you want, for as long as you want. No limits on storage or features' },
      ],
    },
  ]

  const Wrapper = embedded ? ({ children }: { children: React.ReactNode }) => <>{children}</> : PublicLayout

  return (
    <Wrapper>
      <div className="flex-1 flex flex-col items-center p-4 py-8 md:py-12">
        <div className="w-full max-w-3xl">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-3">Features</h1>
            <p className="text-[var(--text-secondary)] max-w-xl mx-auto">
              Everything you need to understand your relationship with alcohol — private, secure, and completely free.
            </p>
          </div>

          {/* Feature Sections */}
          <div className="space-y-6">
            {sections.map((section) => (
              <div key={section.title} id={section.id} className="card scroll-mt-24">
                <div className="flex items-start gap-3 mb-4">
                  <span className="w-10 h-10 rounded-lg bg-[var(--bg-primary)] flex items-center justify-center shrink-0 text-[var(--accent-teal)]">
                    {section.icon}
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--text-primary)]">{section.title}</h2>
                    <p className="text-sm text-[var(--text-secondary)]">{section.subtitle}</p>
                  </div>
                </div>
                <div className="space-y-3 ml-[52px]">
                  {section.features.map((feature) => (
                    <div key={feature.title}>
                      <p className="text-[var(--text-primary)] text-sm font-medium">{feature.title}</p>
                      <p className="text-sm text-[var(--text-secondary)]">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <p className="text-[var(--text-secondary)] mb-4">
              Change the flag in the navigation bar to see features for your country.
            </p>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}
